import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, Loader2 } from "lucide-react";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { Footer } from "@/components/Footer";
import * as Sentry from "@sentry/react";

const getCurrencies = (t: TFunction) => [
  { code: "USD", name: t('currencies.usd') },
  { code: "EUR", name: t('currencies.eur') },
  { code: "GBP", name: t('currencies.gbp') },
  { code: "JPY", name: t('currencies.jpy') },
  { code: "CAD", name: t('currencies.cad') },
  { code: "AUD", name: t('currencies.aud') },
  { code: "CHF", name: t('currencies.chf') },
];

const getCountries = (t: TFunction) => [
  t('countries.us'), t('countries.uk'), t('countries.france'), t('countries.germany'),
  t('countries.spain'), t('countries.italy'), t('countries.canada'), t('countries.australia'),
  t('countries.japan'), t('countries.china'), t('countries.india'), t('countries.brazil')
];

export default function Profile() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [defaultCountry, setDefaultCountry] = useState("");
  const [defaultCurrency, setDefaultCurrency] = useState("USD");

  const CURRENCIES = getCurrencies(t);
  const COUNTRIES = getCountries(t);

  const getErrorMessage = useCallback(
    (error: unknown) => {
      if (error instanceof Error) {
        return error.message;
      }
      if (typeof error === "string") {
        return error;
      }
      return t("errors.genericError");
    },
    [t]
  );

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/auth");
    }
  }, [user, authLoading, navigate]);

  const loadProfile = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("default_country, default_currency")
        .eq("user_id", user?.id)
        .single();

      if (error) throw error;

      if (data) {
        setDefaultCountry(data.default_country || "");
        setDefaultCurrency(data.default_currency || "USD");
      }
    } catch (error) {
      console.error("Error loading profile:", error);
    }
  }, [user?.id]);

  useEffect(() => {
    if (user) {
      loadProfile();
    }
  }, [user, loadProfile]);

  const handleUpdateEmail = async () => {
    if (!newEmail.trim()) {
      toast({
        title: t('errors.title'),
        description: t('profile.invalidEmailError'),
        variant: "destructive",
        duration: Infinity,
      });
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({
        email: newEmail,
      });

      if (error) throw error;

      toast({
        title: t('toast.success'),
        description: t('profile.emailUpdateSuccess'),
      });
      setNewEmail("");
    } catch (error: unknown) {
      const sentryEventId = Sentry.captureException(error, {
        level: 'error',
        tags: {
          component: 'Profile',
          action: 'update_email',
          error_code: '3001', // AUTH_EMAIL_UPDATE_FAILED
        },
        extra: {
          userId: user?.id,
          userEmail: user?.email,
          newEmail: newEmail,
          errorMessage: getErrorMessage(error),
        },
      });

      const supportId = sentryEventId
        ? sentryEventId.substring(0, 8).toUpperCase()
        : `DEV${Date.now().toString(36).toUpperCase().slice(-6)}`;

      toast({
        title: t('errors.title'),
        description: t('errors.emailUpdateFailedWithSupport', { supportId }),
        variant: "destructive",
        duration: Infinity,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword.trim() || newPassword.length < 12) {
      toast({
        title: t('errors.title'),
        description: t('profile.passwordMinLengthError'),
        variant: "destructive",
        duration: Infinity,
      });
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) throw error;

      toast({
        title: t('toast.success'),
        description: t('profile.passwordUpdateSuccess'),
      });
      setNewPassword("");
    } catch (error: unknown) {
      const sentryEventId = Sentry.captureException(error, {
        level: 'error',
        tags: {
          component: 'Profile',
          action: 'update_password',
          error_code: '3002', // AUTH_PASSWORD_UPDATE_FAILED
        },
        extra: {
          userId: user?.id,
          userEmail: user?.email,
          errorMessage: getErrorMessage(error),
        },
      });

      const supportId = sentryEventId
        ? sentryEventId.substring(0, 8).toUpperCase()
        : `DEV${Date.now().toString(36).toUpperCase().slice(-6)}`;

      toast({
        title: t('errors.title'),
        description: t('errors.passwordUpdateFailedWithSupport', { supportId }),
        variant: "destructive",
        duration: Infinity,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateDefaults = async () => {
    setLoading(true);
    try {
      const { error } = await supabase
        .from("profiles")
        .update({
          default_country: defaultCountry || null,
          default_currency: defaultCurrency,
        })
        .eq("user_id", user?.id);

      if (error) throw error;

      toast({
        title: t('toast.success'),
        description: t('profile.preferencesUpdateSuccess'),
      });
    } catch (error: unknown) {
      const sentryEventId = Sentry.captureException(error, {
        level: 'error',
        tags: {
          component: 'Profile',
          action: 'update_preferences',
          error_code: '5002', // PROFILE_UPDATE_FAILED
        },
        extra: {
          userId: user?.id,
          userEmail: user?.email,
          defaultCountry,
          defaultCurrency,
          errorMessage: getErrorMessage(error),
        },
      });

      const supportId = sentryEventId
        ? sentryEventId.substring(0, 8).toUpperCase()
        : `DEV${Date.now().toString(36).toUpperCase().slice(-6)}`;

      toast({
        title: t('errors.title'),
        description: t('errors.preferencesUpdateFailedWithSupport', { supportId }),
        variant: "destructive",
        duration: Infinity,
      });
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 border-b bg-card shadow-sm">
        <div className="flex items-center gap-4 px-4 py-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/")}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-lg font-bold">{t('profile.title')}</h1>
        </div>
      </header>

      <div className="container max-w-2xl mx-auto p-4 space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>{t('profile.accountInformation')}</CardTitle>
            <CardDescription>{t('profile.currentEmail')}</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {t('profile.accountInformationGuide')}
            </p>
            <div className="space-y-2">
              <Label>{t('profile.emailAddress')}</Label>
              <Input value={user?.email || ""} disabled />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('profile.updateEmailTitle')}</CardTitle>
            <CardDescription>{t('profile.updateEmailDescription')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground leading-relaxed">
              {t('profile.updateEmailGuide')}
            </p>
            <div className="space-y-2">
              <Label htmlFor="new-email">{t('profile.newEmailAddress')}</Label>
              <Input
                id="new-email"
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder={t('profile.enterNewEmail')}
              />
            </div>
            <Button onClick={handleUpdateEmail} disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('buttons.updateEmail')}
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('profile.resetPasswordTitle')}</CardTitle>
            <CardDescription>{t('profile.resetPasswordDescription')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground leading-relaxed">
              {t('profile.resetPasswordGuide')}
            </p>
            <div className="space-y-2">
              <Label htmlFor="new-password">{t('auth.newPassword')}</Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder={t('auth.enterNewPassword')}
              />
            </div>
            <Button onClick={handleResetPassword} disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('buttons.resetPassword')}
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('profile.defaultSettings')}</CardTitle>
            <CardDescription>{t('profile.defaultSettingsDescription')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground leading-relaxed">
              {t('profile.defaultSettingsGuide')}
            </p>
            <div className="space-y-2">
              <Label htmlFor="default-country">{t('profile.defaultCountry')}</Label>
              <Select value={defaultCountry} onValueChange={setDefaultCountry}>
                <SelectTrigger id="default-country">
                  <SelectValue placeholder={t('profile.selectCountry')} />
                </SelectTrigger>
                <SelectContent>
                  {COUNTRIES.map((country) => (
                    <SelectItem key={country} value={country}>
                      {country}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="default-currency">{t('profile.defaultCurrency')}</Label>
              <Select value={defaultCurrency} onValueChange={setDefaultCurrency}>
                <SelectTrigger id="default-currency">
                  <SelectValue placeholder={t('profile.selectCurrency')} />
                </SelectTrigger>
                <SelectContent>
                  {CURRENCIES.map((currency) => (
                    <SelectItem key={currency.code} value={currency.code}>
                      {currency.code} - {currency.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button onClick={handleUpdateDefaults} disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('buttons.savePreferences')}
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('profile.languagePreferencesTitle', 'Language Preferences')}</CardTitle>
            <CardDescription>{t('profile.languagePreferencesDescription', 'Choose your preferred language for the interface')}</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="mb-4 text-sm text-muted-foreground leading-relaxed">
              {t('profile.languagePreferencesGuide', 'Select the language that feels most comfortable so all navigation, prompts, and alerts are easy to follow. You can change this setting at any time, and the interface will refresh instantly to match your choice across every device where you use Receipt Decoder.')}
            </p>
            <LanguageSwitcher />
          </CardContent>
        </Card>
      </div>
      <Footer />
    </div>
  );
}
